import { DataResponse, Dimension, Measure } from '@embeddable.com/core';
import { Theme } from '../../../../theme/theme.types';
import { ChartData, ChartOptions } from 'chart.js';
import { getThemeFormatter } from '../../../../theme/formatter/formatter.utils';
import { getChartColors, getStyleNumber } from '@embeddable.com/remarkable-ui';
import { getDimensionMeasureColor } from '../../../../theme/styles/styles.utils';
import { mergician } from 'mergician';
import { isColorValid, setColorAlpha } from '../../../../utils/color.utils';
import { getDimensionWithoutTruncation } from '../../charts.utils';

export const getLineChartProData = (
  props: {
    data: DataResponse['data'];
    dimension: Dimension;
    measures: Measure[];
    hasMinMaxYAxisRange: boolean;
  },
  theme: Theme,
): ChartData<'line'> => {
  if (!props.data) {
    return {
      labels: [],
      datasets: [{ data: [] }],
    };
  }

  const themeFormatter = getThemeFormatter(theme);

  const groupedData = props.data;

  return {
    labels: groupedData.map((item) => {
      return item[props.dimension.name];
    }),
    datasets: props.measures.map((measure, index) => {
      const zeroFill = Boolean(measure.inputs?.['connectGaps']);
      const values = groupedData.map((item) => item[measure.name] ?? (zeroFill ? 0 : null));

      const lineColor = measure.inputs?.['lineColor'];
      const chartColors = getChartColors();
      const backgroundColor = isColorValid(lineColor)
        ? lineColor
        : getDimensionMeasureColor({
            dimensionOrMeasure: measure,
            theme,
            color: 'background',
            value: measure.name,
            chartColors,
            index,
          });

      const borderColor = isColorValid(lineColor)
        ? lineColor
        : getDimensionMeasureColor({
            dimensionOrMeasure: measure,
            theme,
            color: 'border',
            value: measure.name,
            chartColors,
            index,
          });

      return {
        clip: props.hasMinMaxYAxisRange,
        label: themeFormatter.dimensionOrMeasureTitle(measure),
        data: values,
        backgroundColor: setColorAlpha(backgroundColor, 0.5),
        pointBackgroundColor: backgroundColor,
        borderDash: measure.inputs?.dashedLine
          ? [
              getStyleNumber('--em-linechart-line-dash', '0.25rem'),
              getStyleNumber('--em-linechart-line-gap', '0.25rem'),
            ]
          : undefined,
        borderColor,
        fill: Boolean(measure.inputs?.fillUnderLine),
      } as ChartData<'line'>['datasets'][number];
    }),
  };
};

export const getLineChartProOptions = (
  options: {
    dimension: Dimension;
    measures: Measure[];
    data: ChartData<'line'>;
  },
  theme: Theme,
): ChartOptions<'line'> => {
  const { dimension, data, measures } = options;
  const themeFormatter = getThemeFormatter(theme);

  const lineChartOptions: ChartOptions<'line'> = {
    plugins: {
      datalabels: {
        labels: {
          value: {
            formatter: (value: string | number, context) => {
              const measure = measures[context.datasetIndex]!;
              return themeFormatter.data(measure, value);
            },
          },
        },
      },
      tooltip: {
        callbacks: {
          title: (context) => {
            const label = context[0]?.label;
            return themeFormatter.data(getDimensionWithoutTruncation(dimension), label);
          },
          label: (context) => {
            const measure = measures[context.datasetIndex]!;
            const raw = context.raw as number;
            return `${context.dataset.label}: ${themeFormatter.data(measure, raw)}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          callback: (value) => {
            if (!data || !data.labels) return undefined;

            const label = data.labels[Number(value)] as string;
            return themeFormatter.data(dimension, label);
          },
        },
      },
      y: {
        ticks: {
          callback: (value) => {
            return themeFormatter.data(measures[0]!, value);
          },
        },
      },
    },
  };

  return mergician(lineChartOptions, theme.charts?.lineChartDefaultPro?.options || {});
};
